import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import Button from "../../components/Button";
import AppLogo from "../../components/AppLogo/AppLogo";
import FormItem from "../../components/FormItem";
import InputErrorMessage from "../../components/InputErrorMessage";
import InputField from "../../components/InputField";

import { generateRequestConfig } from "../../constants";
import { AuthContext } from "../../contexts/AuthContext";
import useTimer from "../../hooks/useTimer";
import { getAppInfo, handleErrorForms, getIdApp, getDeviceId, ensureCsrfToken } from "../../utils";

import ResetPasswordSkeleton from "../Skeletons/ResetPasswordSkeleton";
import {
  resetPasswordFields,
  getEmailErrorMessages,
  ResetPasswordFormDataType,
} from "./ResetPassword.constants";

type ResetPasswordState = {
  generalErrors: string | undefined;
  appInfo: any;
};


const ResetPassword: React.FC = () => {
  type FormField = "email";

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const { AuthContextState } = useContext(AuthContext);
  const { csrf_token, idApp, referer } = AuthContextState;
  
  const [state, setState] = useState<ResetPasswordState>({
    appInfo: null,
    generalErrors: undefined,
  });
  const isLoaded = useTimer(400);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<ResetPasswordFormDataType>();

  useEffect(() => {
    document.title = t("Reset password");
    
    if (!getIdApp()) {
      navigate('/');
    }

    if(!appInfo)
    {
      getAppInfo((data) => setState(prevState => ({ ...prevState, appInfo: data })));
    }

  }, [t, i18n]);

  const handleSubmitResetPasswordForm: SubmitHandler<
    ResetPasswordFormDataType
  > = async (data) => {
    const csrfToken = await ensureCsrfToken(csrf_token);
    
    setState((prevState) => ({ ...prevState, generalErrors: undefined }));

    const requestConfigRegistration = generateRequestConfig(
      "post",
      "resetpassword",
      {
        deviceId: getDeviceId(),
        appId: idApp,
        ...data,
      },
      {
        "X-CSRF-TOKEN": csrfToken,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    try {
      const res = await axios(requestConfigRegistration);
      
      if (res.status === 200) {
        const encodedEmail = encodeURIComponent(data.email);
        navigate(
          `/${currentLang}/activation/?appId=${idApp}&referer=${referer}&email=${encodedEmail}&confirm=confirm`
        );
      }
      
    } catch (error) {
      const errorAction = handleErrorForms(error);
      if (errorAction) {
        const { message, field } = errorAction;
        if (field) {
          setError(field as FormField, {
            type: "server",
            message: message,
          });
        } else {
          setState((prevState) => ({ ...prevState, generalErrors: message }));
        }
      }
    }
  };

  const emailErrorMessages = getEmailErrorMessages(errors);
  const { appInfo, generalErrors } = state;
  return (
    <>
      {!isLoaded ? (
        <ResetPasswordSkeleton />
      ) : (
        <>
          <h1 className="text-center auth-title-form">
            {t("Reset password")}
            <AppLogo
              src={appInfo?.urlImage}
              name={appInfo?.name}
              frameClassName="auth-title-logo-frame"
              imageClassName="auth-title-logo"
              placeholderClassName="auth-title-logo-placeholder"
            />
          </h1>
          <div className="card-body">
            <form noValidate onSubmit={handleSubmit(handleSubmitResetPasswordForm)}>
             
              <FormItem
                label={t(resetPasswordFields["email"].label)}
                labelFor={resetPasswordFields["email"].id}
              >
                <InputField
                  autoFocus
                  id={resetPasswordFields["email"].id}
                  type={resetPasswordFields["email"].type}
                  placeholder={t(resetPasswordFields["email"].placeholder)}
                
                   {...register(resetPasswordFields['email'].name, {
                      ...resetPasswordFields['email'].validation,
                      validate: (value) => {
                        return true;
                      },
                      onChange: () => {
                        setState((prevState) => ({ ...prevState, generalErrors: undefined }));
                        clearErrors('email');
                      },
                  })}
                  className={
                    generalErrors || errors.email ? "error" : undefined
                  }
                />
                {errors.email && (
                  <InputErrorMessage
                    msg={t(emailErrorMessages[errors.email.type])}
                  />
                )}
                {generalErrors && <InputErrorMessage msg={t(generalErrors)} />}
              </FormItem>

              <FormItem>
                <Button
                  variant="default"
                  width={100}
                  height={48}
                  text={t("Send")}
                />
              </FormItem>

              <FormItem>
                <Button
                  variant="text"
                  width={100}
                  height={48}
                  text={t("Back")}
                  onClick={() =>
                    navigate(`/${currentLang}/login?appId=${idApp}`)
                  }
                />
              </FormItem>

              <div className="p-3 optext pb-5">
                {t("Continuing you agree with our")}{" "}
                <Link to={`/${currentLang}/terms_and_conditions/`}>
                  {t("Terms and Conditions")}
                </Link>{" "}
                {t("and")}{" "}
                <Link to={`/${currentLang}/privacy_policy/`}>
                  {t("Privacy Policy")}
                </Link>
                .
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default ResetPassword;
